import { useEffect, useState } from "react";
import { CalendarDays, ExternalLink, MapPin, Sparkles, Trophy } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";

import { formatDate } from "@/utils/format";
import { CalendarLinks, matchCalendarItem } from "@/components/CalendarLinks";

export function SponsoredCarousel({ shoots = [], calendar = [] }) {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (shoots.length < 2) return undefined;
    const timer = setInterval(() => setIndex((value) => (value + 1) % shoots.length), 6000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shoots.length]);

  const shoot = shoots[index % shoots.length];
  if (!shoot) return null;
  const linked = matchCalendarItem(shoot, calendar);
  const daysLeft = Math.max(0, Math.ceil((new Date(`${shoot.date}T12:00:00`) - new Date()) / 86400000));
  const posterUrl = `${process.env.REACT_APP_BACKEND_URL}${shoot.poster_url}`;
  const isPdf = shoot.poster_path?.endsWith(".pdf");

  return (
    <div className="floating-panel overflow-hidden p-0" data-testid="sponsored-carousel">
      <AnimatePresence mode="wait">
        <motion.div key={shoot.id} initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }} transition={{ duration: 0.5 }} className="grid sm:grid-cols-[190px_1fr]" data-testid={`sponsored-slide-${shoot.id}`}>
          <a href={posterUrl} target="_blank" rel="noreferrer" className="relative block h-64 bg-charcoal/60 sm:h-full" data-testid="sponsored-poster-link">
            {isPdf ? <span className="grid h-full place-items-center font-mono text-xs uppercase tracking-[0.2em] text-clay">Cartel PDF</span> : <img src={posterUrl} alt={`Cartel de ${shoot.title}`} className="h-full w-full object-cover" />}
            <span className="absolute left-3 top-3 inline-flex items-center gap-1 bg-clay px-2 py-1 font-mono text-[9px] uppercase tracking-[0.22em] text-charcoal"><Sparkles className="h-3 w-3" />Patrocinada</span>
          </a>
          <div className="p-6 sm:p-7">
            <div className="mb-5 flex items-start justify-between gap-6">
              <div>
                <p className="eyebrow">Próxima tirada destacada</p>
                <h2 className="mt-3 font-display text-4xl uppercase leading-[0.86] text-slate-50" data-testid="sponsored-title">{shoot.title}</h2>
              </div>
              <div className="border border-clay/40 bg-charcoal/45 px-4 py-3 text-center backdrop-blur">
                <span className="block font-display text-5xl leading-none text-clay" data-testid="sponsored-countdown">{daysLeft}</span>
                <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-300">días</span>
              </div>
            </div>
            <div className="space-y-3 text-sm text-slate-200">
              <p className="flex items-center gap-3"><CalendarDays className="h-4 w-4 text-clay" />{formatDate(shoot.date)} · {shoot.time}</p>
              <p className="flex items-center gap-3"><MapPin className="h-4 w-4 text-clay" />{shoot.location} · {shoot.province}</p>
              <p className="flex items-center gap-3"><Trophy className="h-4 w-4 text-clay" />{shoot.modality}{shoot.targets ? ` · ${shoot.targets} platos` : ""}{shoot.entry_fee ? ` · ${shoot.entry_fee}` : ""}</p>
            </div>
            <div className="mt-6 flex flex-wrap items-center justify-between gap-4">
              <div className="flex flex-wrap gap-2">
                <a href={posterUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 border border-clay/40 bg-clay/10 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.22em] text-clay transition-colors hover:bg-clay hover:text-charcoal" data-testid="sponsored-open-poster">Ver cartel <ExternalLink className="h-3 w-3" /></a>
                <CalendarLinks item={linked} prefix={`sponsored-results-${shoot.id}`} size="lg" />
              </div>
              {shoots.length > 1 ? (
                <div className="flex gap-2" data-testid="sponsored-dots">
                  {shoots.map((item, dot) => <button key={item.id} type="button" onClick={() => setIndex(dot)} aria-label={`Ver ${item.title}`} className={`h-2 w-6 transition-colors ${dot === index % shoots.length ? "bg-clay" : "bg-white/20"}`} data-testid={`sponsored-dot-${dot}`} />)}
                </div>
              ) : null}
            </div>
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
